import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import FormInputNumber from "../../../../components/form/FormInputNumber";
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import Ln2MappingEditor, {
  Ln2MappingData,
  defaultLn2MappingData,
} from "../../../../components/adapters/Ln2MappingEditor";
import CdoOpcCommand from "./CdoOpcCommand";
// FLATBUFFERS
import { LiftType } from "../../../../../flatbuffers/generated/archive/lift-type";
// INTERFACES
import {
  OpcMappingsInterface,
  OpcCommandType,
  MessageToOpcEntryInterface,
  ResponseContentTypesInterface,
  ReadContentTypesInterface,
  WriteContentTypesInterface,
  OpcCommand,
} from "../../../../../interfaces/AdapterCdoOpc.interface";

export default function CdoOpcMappingMsgToOpcCmd({
  setValue,
  opcMapping,
}: {
  setValue: (newValue: any) => void;
  opcMapping: OpcMappingsInterface;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [msgToOpcCmd, setMsgToOpcCmd] = useState<{
    command: OpcCommand;
    opcCommand: OpcCommandType;
    address: string;
    description: string;
    ln2Mapping: Ln2MappingData;
    active: boolean;
    position: number;
    responseContentType: ResponseContentTypesInterface;
    readContentType: ReadContentTypesInterface;
    writeContentType: WriteContentTypesInterface;
  }>({
    command: {},
    opcCommand: "read_content_table",
    address: "",
    description: "",
    ln2Mapping: defaultLn2MappingData(),
    active: true,
    position: 0,
    responseContentType: "read_item_response",
    readContentType: "read_sync_item_message",
    writeContentType: "make_group",
  });

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickAddNewMsgToOpcCmd = () => {
    const msg = msgToOpcCmd.ln2Mapping.message;
    const newMsgToOpcCmd: MessageToOpcEntryInterface = {
      value: {
        [msgToOpcCmd.opcCommand]:
          msgToOpcCmd.command.read_content_table ||
          msgToOpcCmd.command.response_content_table ||
          msgToOpcCmd.command.write_content_table,
      },
      key: {
        encoded_type: msg?.encodedType ?? 0,
        message_content: msg?.messageContent ?? 0,
        message_value: msg?.messageValue ?? 0,
        fbs_device_type: msg?.deviceType ?? LiftType.ELEVATOR,
        active: msgToOpcCmd.active,
        position: msgToOpcCmd.position,
      },
    };
    const copy = structuredClone(opcMapping);
    copy.msg_to_opc_cmd.push(newMsgToOpcCmd);
    setValue(copy);
    setMsgToOpcCmd({
      command: {},
      opcCommand: "read_content_table",
      address: "",
      description: "",
      ln2Mapping: defaultLn2MappingData(),
      active: true,
      position: 0,
      responseContentType: "read_item_response",
      readContentType: "read_sync_item_message",
      writeContentType: "add_item",
    });
  };

  const onChangeMsgToOpcCmdText = (newValue: string, variableName: string) => {
    const copy = structuredClone(msgToOpcCmd);
    if (variableName === "address") {
      copy.address = newValue;
    } else if (variableName === "description") {
      copy.description = newValue;
    }
    setMsgToOpcCmd(copy);
  };

  const onChangeMsgToOpcCmdActive = (newValue: boolean, _variableName: string) => {
    const copy = structuredClone(msgToOpcCmd);
    copy.active = newValue;
    setMsgToOpcCmd(copy);
  };

  const onChangeMsgToOpcCmdPosition = (newValue: number, _variableName: string) => {
    const copy = structuredClone(msgToOpcCmd);
    copy.position = newValue;
    setMsgToOpcCmd(copy);
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <CdoOpcCommand setOpcCommand={setMsgToOpcCmd} opcCommand={msgToOpcCmd} />
      <FormInputText
        inputValue={msgToOpcCmd.address}
        inputLabelName={t("general.address")}
        inputOnChange={onChangeMsgToOpcCmdText}
        inputVariableName="address"
        isRequired
        disabledInput={false}
      />
      <FormInputText
        inputValue={msgToOpcCmd.description}
        inputLabelName={t("general.description")}
        inputOnChange={onChangeMsgToOpcCmdText}
        inputVariableName="description"
        isRequired
        disabledInput={false}
      />
      <Ln2MappingEditor
        value={msgToOpcCmd.ln2Mapping}
        onChange={(updated) => setMsgToOpcCmd((prev) => ({ ...prev, ln2Mapping: updated }))}
        disabled={false}
        hideBankLift
        renderExtraLn2Fields={() => (
          <>
            <FormInputNumber
              inputValue={msgToOpcCmd.position}
              inputOnChange={onChangeMsgToOpcCmdPosition}
              inputLabelName={t("general.position")}
              inputVariableName="position"
            />
            <FormInputBooleanSwitch
              value={msgToOpcCmd.active}
              onChangeSwitch={onChangeMsgToOpcCmdActive}
              labelName={t("general.active")}
              variableName="active"
              isRequired
              disabledInput={false}
            />
          </>
        )}
      />
      <div className="col-md-12 mt-3">
        <div className="generalStyles__flex">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired" />
          </div>
          <div className="generalStyles__width100">
            <Button
              disabled={msgToOpcCmd.address === "" || msgToOpcCmd.ln2Mapping.message === null}
              onClick={onClickAddNewMsgToOpcCmd}
              className={`generalStyles__width100 ${
                msgToOpcCmd.address === "" || msgToOpcCmd.ln2Mapping.message === null
                  ? ""
                  : "buttonStyle__28"
              }`}
            >
              <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
              {t("general.add")}
            </Button>
          </div>
          <div className="generalStyles__infoTooltipIconBlank" />
        </div>
      </div>
    </div>
  );
}
